import React, { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import styled from 'libs/styled';
import type { ContentSliderDesktopProps, ContentSliderDesktopApi } from './types';

const GAP_DEFAULT = 10;
const filterTransientProps = { shouldForwardProp: (prop: string) => !prop.startsWith('$') };

const SliderContainer = styled('div')`
    position: relative;
    width: 100%;
    grid-column: 1 / -1;
`;

const Viewport = styled('div')`
    position: relative;
    width: 100%;
    overflow: hidden;
`;

const Track = styled('div')`
    position: relative;
    width: 100%;
`;

const ItemSlot = styled('div', filterTransientProps)<{
    $left: number;
    $width: number;
    $isAnimating?: boolean;
}>`
    position: absolute;
    top: 0;
    left: 0;
    transform: translateX(${({ $left }) => $left}px);
    width: ${({ $width }) => $width}px;
    overflow: hidden;
    min-width: 0;
    will-change: transform, width;

    transition: ${({ $isAnimating }) =>
        $isAnimating ? 'transform var(--transition-slider), width var(--transition-slider)' : 'none'};
`;

const circularSlot = (itemIndex: number, activeIdx: number, numberOfItems: number): number => {
    let diff = (((itemIndex - activeIdx) % numberOfItems) + numberOfItems) % numberOfItems;
    if (diff >= Math.ceil(numberOfItems / 2)) diff -= numberOfItems;
    return diff;
};

const isTeleporting = (itemIndex: number, activeIdx: number, prevActiveIdx: number, numberOfItems: number): boolean => {
    if (numberOfItems <= 1 || activeIdx === prevActiveIdx) return false;
    const prevSlot = circularSlot(itemIndex, prevActiveIdx, numberOfItems);
    const currSlot = circularSlot(itemIndex, activeIdx, numberOfItems);
    return Math.abs(currSlot - prevSlot) > 1;
};

interface Ghost<T> {
    itemKey: string | number;
    item: T;
    fromSlot: number;
    toSlot: number;
}

const refKey = (key: string | number): string => String(key);

/** Desktop carousel: one enlarged item, sliding track with prev/next. Exposes nav API via onReady for SliderHeader. */
const ContentSliderDesktop = <T,>({
    items,
    getItemKey,
    renderItem,
    trackHeight: trackHeightProp,
    gap = GAP_DEFAULT,
    onReady,
}: ContentSliderDesktopProps<T>) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [ghosts, setGhosts] = useState<Ghost<T>[]>([]);
    const prevActiveRef = useRef(0);
    const navDirectionRef = useRef<'left' | 'right'>('right');
    const slotElementsRef = useRef<Map<string, HTMLDivElement>>(new Map());
    const ghostElementsRef = useRef<Map<string, HTMLDivElement>>(new Map());
    const navigationTimeoutRef = useRef<number | null>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const viewportRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!viewportRef.current) return;

        if (typeof ResizeObserver !== 'undefined') {
            const resizeObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    setContainerWidth(entry.contentRect.width);
                }
            });
            resizeObserver.observe(viewportRef.current);
            return () => resizeObserver.disconnect();
        }

        const handleResize = () => {
            if (viewportRef.current) {
                setContainerWidth(viewportRef.current.getBoundingClientRect().width);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const columnWidth = containerWidth > 0 ? (containerWidth - 5 * gap) / 6 : 0;
    const activeWidth = 2 * columnWidth + gap;

    const getSlotPosition = useCallback(
        (slot: number): { left: number; width: number } => {
            const isActive = slot === 0;
            const width = isActive ? activeWidth : columnWidth;

            let column: number;
            if (slot <= -2) {
                column = slot + 2;
            } else if (slot === -1) {
                column = 1;
            } else if (slot === 0) {
                column = 3;
            } else {
                column = 4 + slot;
            }

            const left = column * columnWidth + column * gap;
            return { left, width };
        },
        [activeWidth, columnWidth, gap],
    );

    const getSlotPositionRef = useRef(getSlotPosition);
    getSlotPositionRef.current = getSlotPosition;

    const getItemKeyRef = useRef(getItemKey);
    getItemKeyRef.current = getItemKey;

    const trackHeight =
        typeof trackHeightProp === 'function'
            ? trackHeightProp(activeWidth, columnWidth)
            : trackHeightProp ?? activeWidth * (5 / 4);

    const canGoLeft = items.length > 1;
    const canGoRight = items.length > 1;

    const handleNavigate = useCallback(
        (direction: 'left' | 'right') => {
            if (isAnimating) return;
            const numberOfItems = items.length;
            if (numberOfItems <= 1) return;

            navDirectionRef.current = direction;
            prevActiveRef.current = activeIndex;
            const newIndex =
                direction === 'left'
                    ? (activeIndex - 1 + numberOfItems) % numberOfItems
                    : (activeIndex + 1) % numberOfItems;

            const newGhosts: Ghost<T>[] = [];
            items.forEach((item, idx) => {
                if (isTeleporting(idx, newIndex, activeIndex, numberOfItems)) {
                    const prevSlot = circularSlot(idx, activeIndex, numberOfItems);
                    const exitSlot = direction === 'right' ? prevSlot - 1 : prevSlot + 1;
                    newGhosts.push({
                        itemKey: getItemKeyRef.current(item),
                        item,
                        fromSlot: prevSlot,
                        toSlot: exitSlot,
                    });
                }
            });
            setGhosts(newGhosts);

            setIsAnimating(true);
            setActiveIndex(newIndex);

            if (navigationTimeoutRef.current != null) {
                clearTimeout(navigationTimeoutRef.current);
            }
            navigationTimeoutRef.current = window.setTimeout(() => {
                setIsAnimating(false);
                setGhosts([]);
                navigationTimeoutRef.current = null;
            }, 500);
        },
        [activeIndex, items, isAnimating],
    );

    useEffect(
        () => () => {
            if (navigationTimeoutRef.current != null) {
                clearTimeout(navigationTimeoutRef.current);
            }
        },
        [],
    );

    useEffect(() => {
        onReady?.({
            scrollPrev: () => handleNavigate('left'),
            scrollNext: () => handleNavigate('right'),
            canGoPrev: canGoLeft,
            canGoNext: canGoRight,
        });
    }, [onReady, canGoLeft, canGoRight, handleNavigate]);

    useLayoutEffect(() => {
        const numberOfItems = items.length;
        const TRANSITION = 'transform var(--transition-slider), width var(--transition-slider)';

        if (!isAnimating) {
            items.forEach((_, idx) => {
                const slotKey = `slot-${idx}`;
                const el = slotElementsRef.current.get(slotKey);
                if (el) {
                    el.style.transition = '';
                    el.style.transform = '';
                    el.style.width = '';
                }
            });
            return;
        }

        const animations: {
            el: HTMLDivElement;
            startLeft: number;
            startWidth: number;
            endLeft: number;
            endWidth: number;
        }[] = [];

        items.forEach((item, idx) => {
            if (!isTeleporting(idx, activeIndex, prevActiveRef.current, numberOfItems)) return;
            const slotKey = `slot-${idx}`;
            const el = slotElementsRef.current.get(slotKey);
            if (!el) return;

            const entrySlot = navDirectionRef.current === 'right' ? 2 : -3;
            const entryPos = getSlotPositionRef.current(entrySlot);
            const finalSlot = circularSlot(idx, activeIndex, numberOfItems);
            const finalPos = getSlotPositionRef.current(finalSlot);

            animations.push({
                el,
                startLeft: entryPos.left,
                startWidth: entryPos.width,
                endLeft: finalPos.left,
                endWidth: finalPos.width,
            });
        });

        ghosts.forEach((ghost, ghostIndex) => {
            const { fromSlot, toSlot } = ghost;
            const refMapKey = `ghost-${ghostIndex}`;
            const el = ghostElementsRef.current.get(refMapKey);
            if (!el) return;

            const fromPos = getSlotPositionRef.current(fromSlot);
            const toPos = getSlotPositionRef.current(toSlot);

            animations.push({
                el,
                startLeft: fromPos.left,
                startWidth: fromPos.width,
                endLeft: toPos.left,
                endWidth: toPos.width,
            });
        });

        if (animations.length === 0) return;

        animations.forEach(({ el, startLeft, startWidth }) => {
            el.style.transition = 'none';
            el.style.transform = `translateX(${startLeft}px)`;
            el.style.width = `${startWidth}px`;
        });

        animations[0].el.getBoundingClientRect();

        animations.forEach(({ el, endLeft, endWidth }) => {
            el.style.transition = TRANSITION;
            el.style.transform = `translateX(${endLeft}px)`;
            el.style.width = `${endWidth}px`;
        });
    }, [activeIndex, isAnimating, items, ghosts]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!containerRef.current) return;

            // Only handle keyboard when this slider (or a descendant) has focus,
            // so multiple sliders on the page do not all respond to the same key press.
            const { activeElement } = document;
            if (!activeElement || !containerRef.current.contains(activeElement)) {
                return;
            }

            if (event.key === 'ArrowLeft' && canGoLeft) {
                handleNavigate('left');
            } else if (event.key === 'ArrowRight' && canGoRight) {
                handleNavigate('right');
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [canGoLeft, canGoRight, handleNavigate]);

    const touchStartX = useRef<number | null>(null);
    const touchEndX = useRef<number | null>(null);

    const handleTouchStart = useCallback((event: React.TouchEvent) => {
        touchStartX.current = event.touches[0].clientX;
    }, []);

    const handleTouchMove = useCallback((event: React.TouchEvent) => {
        touchEndX.current = event.touches[0].clientX;
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (touchStartX.current === null || touchEndX.current === null) return;

        const diff = touchStartX.current - touchEndX.current;
        const threshold = 50;

        if (Math.abs(diff) > threshold) {
            if (diff > 0 && canGoRight) {
                handleNavigate('right');
            } else if (diff < 0 && canGoLeft) {
                handleNavigate('left');
            }
        }

        touchStartX.current = null;
        touchEndX.current = null;
    }, [canGoLeft, canGoRight, handleNavigate]);

    if (items.length === 0) {
        return null;
    }

    return (
        <SliderContainer
            ref={containerRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <Viewport ref={viewportRef}>
                <Track style={{ height: typeof trackHeight === 'number' ? `${trackHeight}px` : undefined }}>
                    {items.map((item, idx) => {
                        const numberOfItems = items.length;
                        const slot = circularSlot(idx, activeIndex, numberOfItems);
                        const { left, width } = getSlotPosition(slot);
                        const teleporting = isTeleporting(idx, activeIndex, prevActiveRef.current, numberOfItems);
                        const slotKey = `slot-${idx}`;
                        const reactKey = refKey(getItemKey(item));

                        return (
                            <ItemSlot
                                key={reactKey}
                                ref={(el: HTMLDivElement | null) => {
                                    if (el) slotElementsRef.current.set(slotKey, el);
                                    else slotElementsRef.current.delete(slotKey);
                                }}
                                $left={left}
                                $width={width}
                                $isAnimating={isAnimating && !teleporting}
                            >
                                {renderItem(item, idx, slot === 0)}
                            </ItemSlot>
                        );
                    })}
                    {ghosts.map((ghost, ghostIndex) => {
                        const ghostFromPos = getSlotPosition(ghost.fromSlot);
                        const refMapKey = `ghost-${ghostIndex}`;
                        return (
                            <ItemSlot
                                key={refMapKey}
                                ref={(el: HTMLDivElement | null) => {
                                    if (el) ghostElementsRef.current.set(refMapKey, el);
                                    else ghostElementsRef.current.delete(refMapKey);
                                }}
                                $left={ghostFromPos.left}
                                $width={ghostFromPos.width}
                                $isAnimating={false}
                            >
                                {renderItem(ghost.item, -1, false)}
                            </ItemSlot>
                        );
                    })}
                </Track>
            </Viewport>
        </SliderContainer>
    );
};

export default ContentSliderDesktop;
export type { ContentSliderDesktopProps };
